import { create } from 'zustand';
import { DEFAULT_AI_MODEL_ID, isValidAiModelId } from '@funkey/ai-tools/schemas';

/**
 * In-memory thread metadata for the current session. The authoritative store is
 * Postgres (FUN-4637): the thread list is seeded from `aiChat.list` via
 * `hydrateServerThreads`, and message bodies hydrate into the `Chat` registry
 * from `aiChat.getMessages`. Nothing here is persisted — the thread map is
 * rebuilt from the server on every load.
 *
 * Model is a per-turn fact (stamped on each message server-side), not thread
 * state. `draftModelId` seeds the model the NEXT turn requests and is
 * deliberately NOT persisted to localStorage: a default-model change must reach
 * every client on next load, not stay fossilized in a stale persisted value
 * (FUN-4558).
 */
export type ActivityAiChatThread = {
    id: string;
    title: string;
    createdAt: number;
};

function generateId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function generateThreadId(): string {
    return generateId('t');
}

type ActivityAiChatStore = {
    threads: Record<string, ActivityAiChatThread>;
    /** Model id chosen on the empty/landing surface — applied to the next thread on creation. */
    draftModelId: string;
    /** Set the draft model id (used by the model selector when no thread is active yet). */
    setDraftModelId: (modelId: string) => void;
    /** Create a new (empty) thread record. */
    upsertThread: (id: string, title: string) => void;
    /** Seed/merge the thread list fetched from Postgres. Never clobbers live in-session state. */
    hydrateServerThreads: (
        rows: ReadonlyArray<{
            id: string;
            title: string | null;
            createdAt: number;
        }>,
    ) => void;
    /** Update the title of an existing thread (no-op if missing). */
    setThreadTitle: (id: string, title: string) => void;
    /** Remove a thread record. */
    deleteThread: (id: string) => void;
};

export const useActivityAiChatStore = create<ActivityAiChatStore>()(set => ({
    threads: {},
    draftModelId: DEFAULT_AI_MODEL_ID,
    setDraftModelId: modelId => {
        if (!isValidAiModelId(modelId)) return;
        set(prev => (prev.draftModelId === modelId ? prev : { draftModelId: modelId }));
    },
    upsertThread: (id, title) => {
        set(prev => {
            if (prev.threads[id]) return prev;
            return {
                threads: {
                    ...prev.threads,
                    [id]: { id, title, createdAt: Date.now() },
                },
            };
        });
    },
    hydrateServerThreads: rows => {
        set(prev => {
            const next = { ...prev.threads };
            for (const row of rows) {
                // Keep live in-session state (streamed title) — only add
                // threads we don't already track.
                if (next[row.id]) continue;
                next[row.id] = {
                    id: row.id,
                    title: row.title ?? '',
                    createdAt: row.createdAt,
                };
            }
            return { threads: next };
        });
    },
    setThreadTitle: (id, title) => {
        set(prev => {
            const existing = prev.threads[id];
            if (!existing) return prev;
            if (existing.title === title) return prev;
            return {
                threads: { ...prev.threads, [id]: { ...existing, title } },
            };
        });
    },
    deleteThread: id => {
        set(prev => {
            if (!(id in prev.threads)) return prev;
            const next = { ...prev.threads };
            delete next[id];
            return { threads: next };
        });
    },
}));
